"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import {
  DOCUMENT_FORM_BRIDGE_SCRIPT,
  parseDocumentFormStatusMessage,
  type DocumentFormValues,
} from "../lib/document-form-bridge";

// Fleet-authored agreements are full HTML forms (fields, Print buttons, their
// own CSS and script). Showing them sanitized strips that behaviour, so they
// run in an opaque-origin sandbox instead: no `allow-same-origin` means the
// document cannot read this page's cookies, storage or DOM, and without
// `allow-top-navigation` it cannot redirect the quote page.
export const DOCUMENT_FRAME_SANDBOX =
  "allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox";

export function buildDocumentSrcDoc(html: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '<base target="_blank">' +
    "<style>body{margin:12px;font:14px/1.5 system-ui,sans-serif;color:#374151}</style>" +
    `</head><body>${html}<script>${DOCUMENT_FORM_BRIDGE_SCRIPT}</script></body></html>`
  );
}

export interface DocumentCompletenessStatus {
  complete: boolean;
  /** Required fields and signature canvases still unfilled. */
  missing: number;
}

interface SandboxedDocumentFrameProps {
  title: string;
  content: string;
  className?: string;
  /** Called with what the renter typed or drew inside the document. */
  onFormData?: (values: DocumentFormValues) => void;
  /** Called each time the document finishes loading in front of the renter. */
  onViewed?: () => void;
  /** Called when the document reports whether its required fields are filled. */
  onStatus?: (status: DocumentCompletenessStatus) => void;
}

export function SandboxedDocumentFrame({
  title,
  content,
  className,
  onFormData,
  onViewed,
  onStatus,
}: SandboxedDocumentFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const onFormDataRef = useRef(onFormData);
  onFormDataRef.current = onFormData;
  const onViewedRef = useRef(onViewed);
  onViewedRef.current = onViewed;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // The frame has an opaque origin, so `event.origin` is "null": the
      // sender window is what identifies this document.
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;

      const message = parseDocumentFormStatusMessage(event.data);
      if (!message) return;
      onFormDataRef.current?.(message.values);
      if (message.complete !== undefined) {
        onStatusRef.current?.({
          complete: message.complete,
          missing: message.missing ?? (message.complete ? 0 : 1),
        });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setExpanded(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [expanded]);

  // Expanding only swaps CSS classes: the iframe keeps its element and React
  // position, because moving it in the DOM would reload the document and lose
  // what the renter typed or drew.
  return (
    <div
      className={
        expanded ? "fixed inset-0 z-50 flex flex-col bg-white p-3" : undefined
      }
    >
      <div className="mb-1 flex justify-end">
        <button
          type="button"
          aria-label={expanded ? "Close" : "Expand"}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        >
          {expanded ? "Close" : "Expand"}
        </button>
      </div>
      <iframe
        ref={frameRef}
        title={title}
        sandbox={DOCUMENT_FRAME_SANDBOX}
        srcDoc={buildDocumentSrcDoc(content)}
        onLoad={() => onViewedRef.current?.()}
        className={cn(
          "h-[min(80vh,900px)] w-full rounded border border-gray-100 bg-white",
          className,
          expanded && "h-full flex-1",
        )}
      />
    </div>
  );
}
